import { useEffect, useCallback, useRef } from 'react';

import { IS_DEMO } from 'constants/constants';
import { addLogsToSentry } from 'helpers/sentry';
import { Fn } from 'types/utils';

const CONNECTION_CHECK_ENDPOINT = `${IS_DEMO ? '/app' : ''}/favicon.ico`;
const API_CHECK_INTERVAL = 10000; // 10 sec
const useAppOnlineStatus = (onlineFn: Fn, offlineFn: Fn): void => {
  /**
   * Due to bug https://bugs.chromium.org/p/chromium/issues/detail?id=678075,
   * it's not reliable to detect if the browser is currently online or offline
   * based on `navigator.onLine`.
   * As a work around:
   * 1. We change the status upon `online` or `offline` events.
   * 2. We perform a HEAD request to /favicon.ico to verify connectivity after the online or offline event.
   * 3. We also check connectivity when the tab regains focus after being idle.
   * 4. We periodically check connectivity every 10 seconds when offline.
   */

  const previousVisibilityState = useRef<'hidden' | 'visible' | null>(null);
  const offlineCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCheckingConnectionRef = useRef(false);

  const checkNetworkConnection = useCallback(async () => {
    if (isCheckingConnectionRef.current) return;

    try {
      isCheckingConnectionRef.current = true;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(CONNECTION_CHECK_ENDPOINT, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        onlineFn();
        // Clear interval when back online
        if (offlineCheckIntervalRef.current) {
          clearInterval(offlineCheckIntervalRef.current);
          offlineCheckIntervalRef.current = null;
        }
      } else {
        offlineFn();
        if (!offlineCheckIntervalRef.current) {
          offlineCheckIntervalRef.current = setInterval(() => {
            checkNetworkConnection();
          }, API_CHECK_INTERVAL);
        }
      }
    } catch (error) {
      const isNetworkError =
        error instanceof TypeError ||
        (error instanceof Error && error.name === 'AbortError');
      if (!isNetworkError) {
        addLogsToSentry(null, error as Error);
      }
      offlineFn();
    } finally {
      isCheckingConnectionRef.current = false;
    }
  }, [onlineFn, offlineFn]);

  const handleOnlineStatus = useCallback(() => {
    checkNetworkConnection();
  }, [checkNetworkConnection]);

  const handleVisibilityChange = useCallback(() => {
    const currentVisibility = document.visibilityState;

    if (currentVisibility === 'visible' && previousVisibilityState.current === 'hidden') {
      checkNetworkConnection();
    }

    previousVisibilityState.current = currentVisibility;
  }, [checkNetworkConnection]);

  useEffect(() => {
    previousVisibilityState.current = document.visibilityState;

    window?.addEventListener('online', handleOnlineStatus);
    window?.addEventListener('offline', handleOnlineStatus);
    document?.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window?.removeEventListener('online', handleOnlineStatus);
      window?.removeEventListener('offline', handleOnlineStatus);
      document?.removeEventListener('visibilitychange', handleVisibilityChange);
      // clean up interval on unmount
      if (offlineCheckIntervalRef.current) {
        clearInterval(offlineCheckIntervalRef.current);
      }
    };
  }, [handleOnlineStatus, handleVisibilityChange]);
};

export default useAppOnlineStatus;
